import React from 'react'

import { Modal, Button, Form, Col, Row } from '_zari/core'
import { ZariContext } from '_zari/context'

import * as AlmacenService from 'services/almacen.service'
import * as EstanteriaService from 'services/estanteria.service'
import * as UbicacionService from 'services/ubicacionAlmacenamiento.service'

import { etiquetaCelda, filaALetra } from 'pages/ubicacionesAlmacenamiento/lista/utils/etiqueta'

import 'pages/ubicacionesAlmacenamiento/lista/styles.css'

const SeleccionUbicacionModal = ({
  show,
  handleClose,
  onSeleccionado,
  valorActual,
  nombreProducto,
  cantidad
}) => {
  const __zariContext = React.useContext(ZariContext)

  const [almacenes, setAlmacenes] = React.useState([])
  const [idAlmacen, setIdAlmacen] = React.useState(valorActual?._idAlmacen || '')

  const [estanterias, setEstanterias] = React.useState([])
  const [estanteriaSel, setEstanteriaSel] = React.useState(null)

  const [ubicaciones, setUbicaciones] = React.useState([])
  const [profundidadActiva, setProfundidadActiva] = React.useState(1)

  const [celdaPick, setCeldaPick] = React.useState(null)

  // Cargar almacenes una vez
  React.useEffect(() => {
    AlmacenService.getAlmacenes_Select()
      .then(({ data }) => setAlmacenes(data || []))
      .catch(() => setAlmacenes([]))
  }, [])

  // Cargar estanterías cuando cambia el almacén
  React.useEffect(() => {
    if (!idAlmacen) {
      setEstanterias([])
      setEstanteriaSel(null)
      return
    }
    EstanteriaService.obtenerTodo(idAlmacen)
      .then(({ data }) => setEstanterias(data || []))
      .catch(() => setEstanterias([]))
    setEstanteriaSel(null)
  }, [idAlmacen])

  // Cargar ubicaciones cuando se elige una estantería
  React.useEffect(() => {
    if (!estanteriaSel) {
      setUbicaciones([])
      return
    }
    UbicacionService.obtenerxEstanteria(estanteriaSel._id)
      .then(({ data }) => setUbicaciones(data || []))
      .catch(() => setUbicaciones([]))
    setProfundidadActiva(1)
  }, [estanteriaSel])

  // Si traemos un valor previo, autoseleccionar la estantería+celda al cargar las estanterías
  React.useEffect(() => {
    if (!valorActual?._idUbicacion || estanterias.length === 0 || estanteriaSel) return
    const est = estanterias.find((e) => e._id === valorActual._idEstanteria)
    if (est) setEstanteriaSel(est)
  }, [valorActual, estanterias, estanteriaSel])

  React.useEffect(() => {
    if (!valorActual?._idUbicacion || ubicaciones.length === 0) return
    const u = ubicaciones.find((x) => x._id === valorActual._idUbicacion)
    if (u) {
      setProfundidadActiva(u.profundidad || 1)
      setCeldaPick(u)
    }
  }, [valorActual, ubicaciones])

  const mapaCeldas = React.useMemo(() => {
    const m = {}
    for (const u of ubicaciones || []) {
      m[`${u.fila}|${u.columna}|${u.profundidad}`] = u
    }
    return m
  }, [ubicaciones])

  const confirmar = () => {
    if (!celdaPick) {
      __zariContext.snack('error', 'Selecciona una celda para continuar')
      return
    }
    if (celdaPick.bloqueado) {
      __zariContext.snack('error', 'La celda está bloqueada')
      return
    }
    onSeleccionado({
      _idAlmacen: idAlmacen,
      _idEstanteria: estanteriaSel._id,
      _idUbicacion: celdaPick._id,
      codigoUbicacion: celdaPick.codigo,
      etiquetaUbicacion: etiquetaCelda(
        celdaPick.fila,
        celdaPick.columna,
        celdaPick.profundidad,
        estanteriaSel.profundidad || 1
      ),
      nombreAlmacen: almacenes.find((a) => a._id === idAlmacen)?.nombre || '',
      nombreEstanteria: estanteriaSel.nombre,
      codigoEstanteria: estanteriaSel.codigo
    })
  }

  const filas = estanteriaSel?.filas || 1
  const columnas = estanteriaSel?.columnas || 1
  const profundidad = estanteriaSel?.profundidad || 1
  const colorEst = estanteriaSel?.color || '#1F3A8A'

  const filasArr = []
  for (let f = filas; f >= 1; f--) filasArr.push(f)
  const columnasArr = []
  for (let c = 1; c <= columnas; c++) columnasArr.push(c)

  return (
    <Modal show={show} size='xl'>
      <Modal.Header>
        <Modal.Title>
          <i className='fa fa-map-marker-alt mr-2' style={{ color: '#1F3A8A' }} />
          Seleccionar ubicación física
          {nombreProducto && (
            <small className='ml-2 text-muted'>
              · {nombreProducto}
              {cantidad ? ` · cant. ${cantidad}` : ''}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='mb-3'>
          <Col md={6}>
            <Form.Group>
              <Form.Label text='ALMACÉN *' md={4} alignRightAt='md' />
              <Col md={8}>
                <Form.Select
                  value={idAlmacen}
                  onChange={(e) => setIdAlmacen(e.target.value)}
                  id='select-almacen-mod'
                >
                  <option value=''>Seleccionar</option>
                  {almacenes.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
          </Col>
          {estanteriaSel && (
            <Col md={6} className='text-right'>
              <Button outline color='secondary' size='sm' onClick={() => setEstanteriaSel(null)}>
                <i className='fa fa-arrow-left mr-1' /> Cambiar estantería
              </Button>
            </Col>
          )}
        </Row>

        {!idAlmacen && (
          <div className='ub-empty'>
            <i className='fa fa-warehouse fa-3x' />
            <p>Selecciona un almacén para ver sus estanterías.</p>
          </div>
        )}

        {idAlmacen && !estanteriaSel && (
          <>
            {estanterias.length === 0 ? (
              <div className='ub-empty'>
                <i className='fa fa-archive fa-3x' />
                <p>Este almacén no tiene estanterías registradas.</p>
              </div>
            ) : (
              <div className='ub-grid-estanterias'>
                {estanterias.map((est) => {
                  const ocup =
                    est.totalCeldas > 0
                      ? Math.round((est.celdasOcupadas / est.totalCeldas) * 100)
                      : 0
                  return (
                    <div
                      key={est._id}
                      className='ub-card'
                      onClick={() => setEstanteriaSel(est)}
                    >
                      <div className='ub-card-header' style={{ background: est.color || '#1F3A8A' }}>
                        <div className='ub-card-codigo'>{est.codigo}</div>
                      </div>
                      <div className='ub-card-body'>
                        <h6 className='ub-card-nombre'>{est.nombre}</h6>
                        {est.descripcion && <p className='ub-card-desc'>{est.descripcion}</p>}
                        <div className='ub-card-stats'>
                          <div className='ub-stat'>
                            <span className='ub-stat-num'>
                              {est.filas}×{est.columnas}
                              {est.profundidad > 1 ? `×${est.profundidad}` : ''}
                            </span>
                            <span className='ub-stat-label'>Dimensiones</span>
                          </div>
                          <div className='ub-stat'>
                            <span className='ub-stat-num'>{est.totalCeldas - est.celdasOcupadas}</span>
                            <span className='ub-stat-label'>Libres</span>
                          </div>
                          <div className='ub-stat'>
                            <span className='ub-stat-num' style={{ color: '#28a745' }}>
                              {est.celdasOcupadas}
                            </span>
                            <span className='ub-stat-label'>Ocupadas</span>
                          </div>
                        </div>
                        <div className='ub-progress'>
                          <div
                            className='ub-progress-bar'
                            style={{ width: `${ocup}%`, background: est.color || '#1F3A8A' }}
                          />
                          <span className='ub-progress-text'>{ocup}% ocupado</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {idAlmacen && estanteriaSel && (
          <div className='ub-matriz-wrapper'>
            <div className='ub-matriz-info'>
              <div>
                <strong>{estanteriaSel.nombre}</strong>
                <small className='text-muted ml-2'>
                  {filas} fila{filas > 1 ? 's' : ''} × {columnas} col{columnas > 1 ? 's' : ''}
                  {profundidad > 1 && ` × ${profundidad} prof`}
                </small>
              </div>
              {celdaPick && (
                <div className='ub-matriz-resumen'>
                  Seleccionado:{' '}
                  <strong>
                    {etiquetaCelda(celdaPick.fila, celdaPick.columna, celdaPick.profundidad, profundidad)}
                  </strong>{' '}
                  ({celdaPick.codigo})
                </div>
              )}
            </div>

            {profundidad > 1 && (
              <div className='ub-prof-tabs'>
                <span className='ub-prof-label'>Profundidad:</span>
                {Array.from({ length: profundidad }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type='button'
                    className={`ub-prof-tab ${profundidadActiva === p ? 'activa' : ''}`}
                    onClick={() => setProfundidadActiva(p)}
                    style={
                      profundidadActiva === p
                        ? { background: colorEst, borderColor: colorEst }
                        : {}
                    }
                  >
                    Nivel {p}
                  </button>
                ))}
              </div>
            )}

            <div className='ub-matriz-leyenda'>
              <div className='ub-leyenda-item'>
                <span className='ub-leyenda-box vacia' /> Vacía (recomendada)
              </div>
              <div className='ub-leyenda-item'>
                <span className='ub-leyenda-box ocupada' /> Con productos (también disponible)
              </div>
              <div className='ub-leyenda-item'>
                <span className='ub-leyenda-box bloqueada' /> Bloqueada (no se puede usar)
              </div>
            </div>

            <div className='ub-matriz-cinema'>
              <div className='ub-cinema-marquesina' style={{ background: colorEst }}>
                <i className='fa fa-archive mr-2' /> Frente de la estantería
              </div>
              <div className='ub-cinema-grid'>
                <div className='ub-cinema-fila-cabecera'>
                  <span className='ub-cinema-cabecera-cell' />
                  {columnasArr.map((c) => (
                    <span key={c} className='ub-cinema-cabecera-cell'>
                      {c}
                    </span>
                  ))}
                </div>
                {filasArr.map((f) => (
                  <div key={f} className='ub-cinema-fila'>
                    <span className='ub-cinema-fila-label'>{filaALetra(f)}</span>
                    {columnasArr.map((c) => {
                      const celda = mapaCeldas[`${f}|${c}|${profundidadActiva}`]
                      if (!celda) return <span key={c} className='ub-cinema-celda inexistente' />
                      const numItems = celda.items?.length || 0
                      let claseEstado = 'vacia'
                      if (celda.bloqueado) claseEstado = 'bloqueada'
                      else if (numItems > 0) claseEstado = 'ocupada'
                      const seleccionado = celdaPick && celdaPick._id === celda._id
                      const etq = etiquetaCelda(f, c, profundidadActiva, profundidad)
                      return (
                        <button
                          key={c}
                          type='button'
                          className={`ub-cinema-celda ${claseEstado} ${seleccionado ? 'resaltada' : ''}`}
                          onClick={() => {
                            if (celda.bloqueado) {
                              __zariContext.snack('error', 'Esta celda está bloqueada')
                              return
                            }
                            setCeldaPick(celda)
                          }}
                          title={
                            celda.bloqueado
                              ? `${etq} - Bloqueada`
                              : `${etq} - ${numItems} producto(s)`
                          }
                        >
                          {celda.bloqueado ? (
                            <i className='fa fa-lock' />
                          ) : numItems > 0 ? (
                            <span className='ub-celda-num'>{numItems}</span>
                          ) : (
                            <span className='ub-celda-vacio'>—</span>
                          )}
                          <span className='ub-celda-cod'>{etq}</span>
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button color='secondary' onClick={handleClose}>
          Cancelar
        </Button>
        <Button color='primary' onClick={confirmar} disabled={!celdaPick}>
          <i className='fa fa-check mr-1' />
          Asignar ubicación
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SeleccionUbicacionModal
